import React, { useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { toast } from '../hooks/use-toast';
import { ArrowRight, Sparkles, Loader2, CheckCircle2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function QuoteForm() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', business: '', turnover: '', amount: '', service: '', notes: '' });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast({ title: 'Please fill required fields', description: 'Name, email and phone are required.', variant: 'destructive' });
      return;
    }
    setLoading(true);
    try {
      const payload = { ...form, notes: form.service ? `Service: ${form.service}. ${form.notes || ''}` : form.notes };
      const { data } = await axios.post(`${API}/leads`, payload);
      toast({ title: 'Enquiry received', description: `Thanks ${form.name}, our team will call you within 2 hours. Ref: ${data.id.slice(0,8)}` });
      setForm({ name: '', email: '', phone: '', business: '', turnover: '', amount: '', service: '', notes: '' });
    } catch (err) {
      const msg = err?.response?.data?.detail?.[0]?.msg || err?.response?.data?.detail || 'Something went wrong, please try again.';
      toast({ title: 'Submission failed', description: typeof msg === 'string' ? msg : 'Please check your details.', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const perks = ['60-second application', 'Soft credit check only', 'Dedicated UK account manager', 'No obligation, no fees'];

  return (
    <section id="quote" className="py-24 bg-white scroll-mt-24">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-12 gap-10 items-stretch">
          <div className="lg:col-span-5 lg:pr-6">
            <span className="chip">Free Enquiry</span>
            <h2 className="mt-5 text-4xl md:text-5xl font-bold text-[#0a2540] tracking-tight leading-[1.05]">
              Get your <span className="font-serif-display italic text-teal-600">funding</span> in 24 hours
            </h2>
            <p className="mt-5 text-slate-600 text-lg leading-relaxed">
              Tell us a little about your business and we’ll design a bespoke solution. No obligation, no impact on your credit score.
            </p>
            <ul className="mt-8 space-y-3">
              {perks.map(t => (
                <li key={t} className="flex items-center gap-3 text-slate-700">
                  <div className="h-6 w-6 rounded-full bg-teal-100 flex items-center justify-center">
                    <CheckCircle2 size={14} className="text-teal-600"/>
                  </div>
                  <span className="text-[15px]">{t}</span>
                </li>
              ))}
            </ul>
            <div className="mt-10 p-5 rounded-2xl bg-[#0a2540] text-white">
              <div className="flex items-start gap-3">
                <Sparkles size={18} className="text-teal-400 mt-0.5"/>
                <div>
                  <div className="text-sm font-semibold">Speak to a funding expert</div>
                  <div className="text-xs text-slate-300 mt-1">Call us on <a href="tel:01217586892" className="text-teal-300 font-semibold hover:underline">0121 758 6892</a> — Mon–Fri, 9am–6pm</div>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <form onSubmit={submit} className="bg-slate-50/60 border border-slate-200 rounded-3xl p-7 md:p-10">
              <div className="grid sm:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-slate-700">Full name *</Label>
                  <Input id="name" value={form.name} onChange={(e)=>setForm({...form, name: e.target.value})} className="h-12 bg-white border-slate-300" placeholder="Jane Smith"/>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-slate-700">Email *</Label>
                  <Input id="email" type="email" value={form.email} onChange={(e)=>setForm({...form, email: e.target.value})} className="h-12 bg-white border-slate-300" placeholder="jane@business.co.uk"/>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-slate-700">Phone *</Label>
                  <Input id="phone" value={form.phone} onChange={(e)=>setForm({...form, phone: e.target.value})} className="h-12 bg-white border-slate-300" placeholder="07000 000000"/>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="business" className="text-slate-700">Business name</Label>
                  <Input id="business" value={form.business} onChange={(e)=>setForm({...form, business: e.target.value})} className="h-12 bg-white border-slate-300" placeholder="Your business Ltd"/>
                </div>
                <div className="space-y-2 sm:col-span-2">
                  <Label className="text-slate-700">Which service are you interested in?</Label>
                  <Select value={form.service} onValueChange={(v)=>setForm({...form, service: v})}>
                    <SelectTrigger className="h-12 bg-white border-slate-300">
                      <SelectValue placeholder="Select a service"/>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Business Loans">Business Loans</SelectItem>
                      <SelectItem value="Merchant Cash Advance">Merchant Cash Advance</SelectItem>
                      <SelectItem value="Card Terminals">Card Terminals</SelectItem>
                      <SelectItem value="EPOS Systems">EPOS Systems</SelectItem>
                      <SelectItem value="Business Funding">Business Funding</SelectItem>
                      <SelectItem value="Commercial Finance">Commercial Finance</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-700">Monthly turnover</Label>
                  <Select value={form.turnover} onValueChange={(v)=>setForm({...form, turnover: v})}>
                    <SelectTrigger className="h-12 bg-white border-slate-300">
                      <SelectValue placeholder="Select range"/>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0-5k">£0 – £5,000</SelectItem>
                      <SelectItem value="5-15k">£5,000 – £15,000</SelectItem>
                      <SelectItem value="15-50k">£15,000 – £50,000</SelectItem>
                      <SelectItem value="50-150k">£50,000 – £150,000</SelectItem>
                      <SelectItem value="150k+">£150,000+</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-700">Funding amount</Label>
                  <Select value={form.amount} onValueChange={(v)=>setForm({...form, amount: v})}>
                    <SelectTrigger className="h-12 bg-white border-slate-300">
                      <SelectValue placeholder="How much do you need?"/>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1-10k">£1,000 – £10,000</SelectItem>
                      <SelectItem value="10-50k">£10,000 – £50,000</SelectItem>
                      <SelectItem value="50-150k">£50,000 – £150,000</SelectItem>
                      <SelectItem value="150-500k">£150,000 – £500,000</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="mt-5 space-y-2">
                <Label htmlFor="notes" className="text-slate-700">Anything else we should know?</Label>
                <Textarea id="notes" rows={4} value={form.notes} onChange={(e)=>setForm({...form, notes: e.target.value})} className="bg-white border-slate-300" placeholder="Optional – tell us about your goals"/>
              </div>

              <div className="mt-8 flex flex-wrap items-center justify-between gap-4">
                <p className="text-xs text-slate-500 max-w-md">By submitting this form you agree with our Privacy Policy. We’ll never share your data.</p>
                <Button type="submit" disabled={loading} className="bg-[#0a2540] hover:bg-[#0a2540]/90 text-white rounded-lg h-12 px-7 font-semibold group disabled:opacity-70">
                  {loading ? (
                    <><Loader2 size={16} className="mr-2 animate-spin"/> Submitting...</>
                  ) : (
                    <>Send Enquiry<ArrowRight size={16} className="ml-2 group-hover:translate-x-0.5 transition-transform"/></>
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
